import React from 'react';
import PropTypes from 'prop-types';
import { LuBadgeCheck, LuBuilding2, LuCircleCheck, LuFileText, LuFolderKanban, LuGraduationCap, LuMail, LuUserCog } from 'react-icons/lu';
import DetailPane from '../../../../components/data/DetailPane';
import useEducationReportText from '../educationReportText';
import { ProgressBar, STATUS_META, StatusBadge, initialsOf } from './ReportBits';

const SECTION_TITLE = 'tw-px-2 tw-pb-1.5 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400 dark:tw-text-slate-500';

const ItemRow = ({ item }) => {
  const { label, typeLabel, deadlineLabel } = useEducationReportText();
  const Icon = item.type === 'SOP' ? LuFileText : LuGraduationCap;
  const deadline = item.done ? '' : deadlineLabel(item.dueDate);
  return (
    <li className={`tw-flex tw-items-start tw-gap-2.5 tw-rounded-xl tw-px-2.5 tw-py-2 ${item.overdue ? 'tw-bg-rose-50 dark:tw-bg-rose-500/[0.07]' : ''}`}>
      <span className={`tw-mt-0.5 tw-grid tw-h-7 tw-w-7 tw-shrink-0 tw-place-items-center tw-rounded-lg ${item.done ? 'tw-bg-emerald-500/10 tw-text-emerald-600 dark:tw-text-emerald-300' : 'tw-bg-slate-900/[0.05] tw-text-slate-500 dark:tw-bg-white/[0.07] dark:tw-text-slate-300'}`}>
        {item.done ? <LuCircleCheck aria-hidden="true" className="tw-h-4 tw-w-4" /> : <Icon aria-hidden="true" className="tw-h-4 tw-w-4" />}
      </span>
      <span className="tw-min-w-0 tw-flex-1">
        <span className="tw-block tw-text-sm tw-font-medium tw-leading-snug tw-text-slate-800 dark:tw-text-slate-100">{item.name || '-'}</span>
        <span className="tw-mt-0.5 tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-text-[11px] tw-text-slate-500 dark:tw-text-slate-400">
          <span className="tw-font-semibold">{typeLabel(item.type)}</span>
          {item.version && <span>{label('panel.version', { version: item.version })}</span>}
          {deadline && <span className={item.overdue ? 'tw-font-semibold tw-text-rose-600 dark:tw-text-rose-400' : ''}>{deadline}</span>}
        </span>
      </span>
    </li>
  );
};

ItemRow.propTypes = { item: PropTypes.object.isRequired };

/**
 * The selected employee's assignments: progress, what is still pending
 * (with due dates, overdue first) and what is done. Docked beside the table
 * on wide screens, a drawer otherwise.
 */
const ReportDetailPane = ({ docked, height, row, position, showWorkplace, onPrev, onNext, onClose }) => {
  const { label } = useEducationReportText();
  const paneLabel = label('panel.label');

  if (!row) return <DetailPane docked={docked} height={height} open={false} label={paneLabel} onClose={onClose} />;

  const pending = row.items.filter((item) => !item.done).sort((a, b) => Number(b.overdue) - Number(a.overdue));
  const done = row.items.filter((item) => item.done);

  return (
    <DetailPane
      docked={docked}
      height={height}
      open
      label={paneLabel}
      heading={label('panel.heading')}
      position={position}
      contentKey={row.id}
      identity={{
        avatar: initialsOf(row),
        gradient: STATUS_META[row.status].gradient,
        title: row.name || '-',
        subtitle: row.role || label('table.noRole'),
        badges: <StatusBadge status={row.status} />,
      }}
      details={[
        { key: 'role', icon: LuUserCog, label: label('panel.role'), value: row.role || label('table.noRole') },
        ...(showWorkplace ? [
          { key: 'department', icon: LuBuilding2, label: label('panel.department'), value: row.department || '-' },
          { key: 'projects', icon: LuFolderKanban, label: label('panel.projects'), value: row.projects.join(', ') || '-' },
        ] : []),
        { key: 'email', icon: LuMail, label: label('panel.email'), value: row.email, href: row.email ? `mailto:${row.email}` : undefined, copyable: Boolean(row.email) },
      ]}
      onPrev={onPrev}
      onNext={onNext}
      onClose={onClose}
    >
      <section className="tw-px-1">
        <p className={SECTION_TITLE}>{label('panel.summary', { done: row.done, total: row.total })}</p>
        <div className="tw-px-1"><ProgressBar done={row.done} total={row.total} status={row.status} /></div>
      </section>

      <section aria-label={label('panel.pending')} className="tw-px-1">
        <p className={SECTION_TITLE}>{label('panel.pending')} · {pending.length}</p>
        {pending.length === 0 ? (
          <p className="tw-flex tw-items-center tw-gap-2 tw-px-2 tw-text-sm tw-font-medium tw-text-emerald-700 dark:tw-text-emerald-300">
            <LuBadgeCheck aria-hidden="true" className="tw-h-4 tw-w-4" />
            {label('panel.allDone')}
          </p>
        ) : (
          <ul className="tw-space-y-0.5">{pending.map((item) => <ItemRow key={`${item.type}-${item.id}`} item={item} />)}</ul>
        )}
      </section>

      <section aria-label={label('panel.completed')} className="tw-px-1">
        <p className={SECTION_TITLE}>{label('panel.completed')} · {done.length}</p>
        {done.length === 0 ? (
          <p className="tw-px-2 tw-text-sm tw-italic tw-text-slate-400">{label('panel.noneDone')}</p>
        ) : (
          <ul className="tw-space-y-0.5">{done.map((item) => <ItemRow key={`${item.type}-${item.id}`} item={item} />)}</ul>
        )}
      </section>
    </DetailPane>
  );
};

ReportDetailPane.propTypes = {
  docked: PropTypes.bool,
  height: PropTypes.number,
  row: PropTypes.object,
  position: PropTypes.shape({ index: PropTypes.number, total: PropTypes.number }),
  showWorkplace: PropTypes.bool,
  onPrev: PropTypes.func,
  onNext: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};

export default ReportDetailPane;
